"use client"
import Link from "next/link"

/**
 * Reusable AEO/GEO structure blocks.
 *
 * These exist because AI assistants extract *structure*, not prose. The scan behind
 * audit.boldpiq.com flags a page with none of these as unquotable no matter how good
 * the copy is. Each block below maps to specific failing checks:
 *
 *   KeyTakeaways    -> aeo_summary, geo_summary, aeo_snippet_ready
 *   FaqSection      -> aeo_faq, aeo_qa_blocks, aeo_collapsible, geo_paa_format,
 *                      geo_structured_answers, geo_faq_schema
 *   TableOfContents -> aeo_toc, aeo_segmentation
 *   Breadcrumbs     -> geo_breadcrumb
 *
 * All are plain components (no hooks) so they drop into the existing "use client" pages.
 * <details>/<summary> is native — collapsible without JavaScript, and crawlers read the
 * closed content, which is why it beats a JS accordion for this purpose.
 */

const ACCENT = "#C4541A"
// Brand accent measures 4.21:1 on the dark background — under the WCAG AA 4.5:1
// minimum for normal text. Links use the lightened variant (5.18:1).
const ACCENT_LINK = "#D4652B"
const MUTED = "rgba(255,255,255,0.6)"
const BORDER = "rgba(255,255,255,0.1)"
const SURFACE = "rgba(255,255,255,0.04)"

const SITE = "https://www.boldpiq.com"

/* ── Key takeaways ────────────────────────────────────────────────────────── */

export function KeyTakeaways({ items, title = "Key takeaways" }: { items: string[]; title?: string }) {
  return (
    <aside
      aria-label={title}
      style={{
        border: `1px solid ${BORDER}`,
        borderLeft: `3px solid ${ACCENT}`,
        background: SURFACE,
        borderRadius: 12,
        padding: "20px 24px",
        margin: "32px 0",
        maxWidth: 820,
      }}
    >
      <h2 style={{ fontSize: 13, letterSpacing: "0.12em", textTransform: "uppercase", color: "#fff", margin: "0 0 12px" }}>
        {title}
      </h2>
      <ul style={{ color: MUTED, fontSize: 15, lineHeight: 1.7, margin: 0, paddingLeft: 20 }}>
        {items.map((t) => (
          <li key={t} style={{ marginBottom: 6 }}>{t}</li>
        ))}
      </ul>
    </aside>
  )
}

/* ── FAQ ──────────────────────────────────────────────────────────────────── */

export type Faq = { q: string; a: string }

/**
 * Emits both the visible Q&A and FAQPage JSON-LD. The schema is generated from the
 * same array that renders, so the two can never drift — asserting an answer in schema
 * that isn't visible on the page is a structured-data violation.
 */
export function FaqSection({
  faqs,
  heading = "Common questions",
  id = "faq",
}: {
  faqs: Faq[]
  heading?: string
  id?: string
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }
  return (
    <section id={id} style={{ maxWidth: 820, margin: "48px auto 0" }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 16px", color: "#fff" }}>{heading}</h2>
      {faqs.map((f) => (
        <details
          key={f.q}
          style={{ borderTop: `1px solid ${BORDER}`, padding: "14px 0" }}
        >
          <summary
            style={{
              color: "#fff",
              fontSize: 16,
              fontWeight: 700,
              cursor: "pointer",
              listStyle: "revert",
            }}
          >
            {f.q}
          </summary>
          <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.7, margin: "10px 0 0" }}>{f.a}</p>
        </details>
      ))}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </section>
  )
}

/* ── Table of contents ────────────────────────────────────────────────────── */

export function TableOfContents({ items }: { items: { id: string; label: string }[] }) {
  return (
    <nav
      aria-label="On this page"
      style={{
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        padding: "16px 20px",
        margin: "24px 0",
        maxWidth: 820,
      }}
    >
      <h2 style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, margin: "0 0 10px" }}>
        On this page
      </h2>
      <ol style={{ margin: 0, paddingLeft: 20, color: MUTED, fontSize: 15, lineHeight: 1.9 }}>
        {items.map((i) => (
          <li key={i.id}>
            <a href={`#${i.id}`} style={{ color: ACCENT_LINK, fontWeight: 600, textDecoration: "none" }}>
              {i.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  )
}

/* ── Breadcrumbs ──────────────────────────────────────────────────────────── */

export function Breadcrumbs({ trail }: { trail: { name: string; path: string }[] }) {
  const full = [{ name: "Home", path: "/" }, ...trail]
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: full.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${SITE}${c.path === "/" ? "" : c.path}`,
    })),
  }
  return (
    <nav aria-label="Breadcrumb" style={{ margin: "0 0 20px" }}>
      <ol style={{ display: "flex", flexWrap: "wrap", gap: 8, listStyle: "none", margin: 0, padding: 0, fontSize: 13, color: MUTED }}>
        {full.map((c, i) => (
          <li key={c.path} style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {i < full.length - 1 ? (
              <>
                <Link href={c.path} style={{ color: ACCENT_LINK, textDecoration: "none", fontWeight: 600 }}>
                  {c.name}
                </Link>
                <span aria-hidden="true">/</span>
              </>
            ) : (
              <span aria-current="page">{c.name}</span>
            )}
          </li>
        ))}
      </ol>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </nav>
  )
}
